import React from 'react';
import { Typography, Button } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import './tables.css';

const { Text } = Typography;

// Mismos límites que valida el backend (GuestRules: OpenTableDto / UpdateOrderGuestsDto)
export const MIN_ADULTS = 1;
export const MAX_ADULTS = 50;
export const MAX_CHILDREN = 20;

// Touch-friendly counter component
const TouchCounter: React.FC<{
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  label: string;
}> = ({ value, onChange, min, max, label }) => {
  const increment = () => {
    if (value < max) {
      onChange(value + 1);
    }
  };

  const decrement = () => {
    if (value > min) {
      onChange(value - 1);
    }
  };

  return (
    <div className="touch-counter-item" style={{ textAlign: 'center' }}>
      <Text className="touch-counter-label" strong style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
        {label}
      </Text>
      <div className="touch-counter-row">
        <Button
          className="touch-counter-btn"
          icon={<MinusOutlined />}
          onClick={decrement}
          disabled={value <= min}
          shape="circle"
          aria-label={`Quitar ${label.toLowerCase()}`}
        />

        <div className="touch-counter-value">
          {value}
        </div>

        <Button
          className="touch-counter-btn"
          icon={<PlusOutlined />}
          onClick={increment}
          disabled={value >= max}
          shape="circle"
          aria-label={`Agregar ${label.toLowerCase()}`}
        />
      </div>
    </div>
  );
};

interface GuestCountersProps {
  adults: number;
  children: number;
  onAdultsChange: (value: number) => void;
  onChildrenChange: (value: number) => void;
  /** Capacidad de la mesa (o del grupo de mesas unidas). */
  capacity: number;
  style?: React.CSSProperties;
}

/**
 * Comensales: contadores de adultos y niños + total contra la capacidad, en una
 * sola fila y sin cards anidadas. Lo usan abrir la mesa y corregir sus
 * comensales. En móvil tables.css los reacomoda (counters centrados, total abajo).
 */
const GuestCounters: React.FC<GuestCountersProps> = ({
  adults, children, onAdultsChange, onChildrenChange, capacity, style,
}) => {
  const total = adults + children;
  const exceedsCapacity = total > capacity;

  return (
    <div
      className="open-table-comensales"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
        margin: '8px 0',
        ...style,
      }}
    >
      <TouchCounter value={adults} onChange={onAdultsChange} min={MIN_ADULTS} max={MAX_ADULTS} label="Adultos" />
      <TouchCounter value={children} onChange={onChildrenChange} min={0} max={MAX_CHILDREN} label="Niños" />
      <div className="open-table-total" style={{ flex: 1, textAlign: 'right', minWidth: 90 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Total</Text>
        <div
          style={{
            fontFamily: 'ui-monospace, Consolas, monospace',
            fontVariantNumeric: 'tabular-nums',
            fontSize: 26,
            fontWeight: 650,
            lineHeight: 1.1,
            color: exceedsCapacity ? 'var(--color-warning-text)' : 'var(--text-primary)',
          }}
        >
          {total}
        </div>
        <Text
          type="secondary"
          style={{ fontSize: 12, color: exceedsCapacity ? 'var(--color-warning-text)' : undefined }}
        >
          {exceedsCapacity ? `supera capacidad (${capacity})` : `de ${capacity}`}
        </Text>
      </div>
    </div>
  );
};

export default GuestCounters;
